import { EmptyState } from '../components/EmptyState.js';
import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router';
import { useBrand } from '../lib/brand-context.js';
import { apiGet } from '../lib/api.js';
import type { DomainUrlResponse, DomainUrlItem } from '../types/index.js';
import { Icon, Spinner } from '../components/ui/Icon.js';
import { useFullBleed } from '../lib/layout-width.js';

export function DomainDetailPage() {
  useFullBleed();
  const { domainId } = useParams<{ domainId: string }>();
  const { selectedBrand, user } = useBrand();
  const [domainInfo, setDomainInfo] = useState<Record<string, unknown> | null>(null);
  const [urls, setUrls] = useState<DomainUrlItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch domain info
  useEffect(() => {
    if (!selectedBrand || !user || !domainId) return;
    let cancelled = false;
    async function load() {
      try {
        const url = `/api/v1/tenants/${selectedBrand!.tenant_id}/brands/${selectedBrand!.id}/sources/domains/${domainId}`;
        const result = await apiGet<{ data: Record<string, unknown> }>(url);
        if (!cancelled) setDomainInfo(result.data);
      } catch {
        // ignore
      }
    }
    load();
    return () => { cancelled = true; };
  }, [selectedBrand, user, domainId]);

  // Fetch URLs
  const fetchUrls = useCallback(async () => {
    if (!selectedBrand || !user || !domainId) return;
    setLoading(true);
    setError(null);
    try {
      const url = `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/sources/urls?domain_id=${domainId}&limit=50`;
      const result = await apiGet<DomainUrlResponse>(url);
      setUrls(result.data);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load URL data');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, user, domainId]);

  useEffect(() => { fetchUrls(); }, [fetchUrls]);

  if (!selectedBrand) {
    return (
      <div className="hub-page--table">
        <h1 className="hub-page__title">Domain detail</h1>
        <p className="text-[var(--hub-color-ink-muted)]">Select a brand first.</p>
      </div>
    );
  }

  return (
    <div data-testid="domain-detail-page" className="hub-page--table" data-density="compact">
      <Link to="/sources" className="inline-flex items-center gap-1 text-sm text-[var(--hub-color-action-text)]">
        <Icon name="bx-left-arrow-alt" /> Back to sources
      </Link>

      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title">
            {domainInfo ? (domainInfo.display_name as string ?? domainInfo.domain as string) : 'Loading...'}
          </h1>
          {domainInfo && (
            <p className="mt-1 text-sm text-[var(--hub-color-ink-muted)]">{domainInfo.domain as string} — {domainInfo.type as string}</p>
          )}
        </div>
      </div>

      {error && <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
      {loading && <div className="flex justify-center py-8"><Spinner className="text-[var(--hub-color-action-text)]" /></div>}

      {!loading && urls.length === 0 && (
        <EmptyState title="No URL data found" description="" testId="no-urls" />
      )}

      {!loading && urls.length > 0 && (
        <>
          <p className="text-sm text-[var(--hub-color-ink-muted)]">{total} URL{total !== 1 ? 's' : ''} found</p>
          <div className="overflow-x-auto">
            <table className="hub-table" data-testid="url-table">
              <thead>
                <tr>
                  <th>URL</th>
                  <th className="is-num">Appearances</th>
                  <th className="is-num">Cited</th>
                  <th className="is-num">Retrieved</th>
                  <th>Engines</th>
                  <th>Last seen</th>
                </tr>
              </thead>
              <tbody>
                {urls.map((item) => (
                  <tr key={item.url}>
                    <td className="max-w-xs truncate">
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[var(--hub-color-action-text)]">
                        {item.url.length > 60 ? item.url.substring(0, 60) + '...' : item.url}
                        <Icon name="bx-link-external" />
                      </a>
                    </td>
                    <td className="is-num">{item.mention_count}</td>
                    <td className="is-num">{item.cited_count}</td>
                    <td className="is-num">{item.retrieved_count}</td>
                    <td>{Array.isArray(item.engines) ? item.engines.join(', ') : (item.engines ?? '')}</td>
                    <td>
                      {item.last_cited ? new Date(item.last_cited).toLocaleDateString() : '--'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
